import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { AlertTriangle } from "lucide-react";

/**
 * Phase H1 — Visible warning when system_settings.mock_email_enabled is OFF
 * and no real email provider is wired. Polls every 60s; only renders for
 * admin / super_admin, since customers can't change the toggle.
 *
 * Phase I — banner is hidden once Resend is configured (mock OFF +
 * email_provider === "resend"), and switches copy when mock OFF + no
 * provider is configured ("emails will fail" vs. the earlier hard-fail
 * 503 copy, both of which apply, but the new copy points at Settings
 * with the right call-to-action).
 */
export function MockEmailModeBanner() {
    const { user } = useAuth();
    // {mockEnabled, providerConfigured} — undefined until first fetch resolves.
    const [state, setState] = useState({ mockEnabled: true, providerConfigured: false, loaded: false });

    useEffect(() => {
        if (!user || user.role === "customer") return;
        let cancelled = false;
        const fetchOnce = async () => {
            try {
                // mock-email-status is the cheapest read; works for both admin
                // & super_admin and includes Phase I's email_provider field.
                const { data } = await api.get("/admin/system-settings/mock-email-status");
                if (cancelled) return;
                setState({
                    mockEnabled: data?.mock_email_enabled !== false,
                    providerConfigured: (data?.email_provider || "mock") !== "mock",
                    loaded: true,
                });
            } catch {
                if (!cancelled) setState((s) => ({ ...s, loaded: true }));
            }
        };
        fetchOnce();
        const t = setInterval(fetchOnce, 60_000);
        return () => { cancelled = true; clearInterval(t); };
    }, [user]);

    if (!user || user.role === "customer" || !state.loaded) return null;
    // Phase I — hide the banner entirely when mock OFF + Resend configured.
    if (!state.mockEnabled && state.providerConfigured) return null;
    // Hide when mock is ON (this is the safe default; no warning needed).
    if (state.mockEnabled) return null;

    return (
        <div
            data-testid="mock-mode-warning-banner"
            className="bg-amber-50 border-b border-amber-200 text-amber-900"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-start sm:items-center gap-3 text-sm">
                <AlertTriangle size={16} className="shrink-0 mt-0.5 sm:mt-0" />
                <div className="flex-1 leading-snug">
                    <span className="font-semibold">Mock mode OFF and no email provider configured</span>{" "}
                    — emails will fail. Configure Resend in System Settings, or re-enable Mock mode.
                </div>
                {user.role === "super_admin" && (
                    <Link
                        to="/admin/settings"
                        className="hidden sm:inline-flex shrink-0 items-center rounded-md bg-amber-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-800"
                        data-testid="mock-mode-warning-cta"
                    >
                        Open Settings
                    </Link>
                )}
            </div>
        </div>
    );
}
